import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, CalendarDays, Clock } from "lucide-react";

const TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00",
];

function nextWeekdays(count = 14) {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (days.length < count) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function fmtDate(d) {
  return d.toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" });
}

export default function BookingModal({ open, onClose }) {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const days = nextWeekdays(10);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Please pick a date and time.");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const iso = new Date(date);
      const dateStr = iso.toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
      const message = `Preferred booking: ${dateStr} at ${time}.\n\nCompany: ${form.company || "-"}\nPhone: ${form.phone || "-"}\n\nNotes: ${form.notes || "-"}`;
      await api.post("/enquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        service: "Strategy call booking",
        source: "booking",
        message,
      });
      toast.success("Booking request sent. Aimee will confirm shortly.");
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email aimee@onboard.nz.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end lg:items-center justify-center p-4" data-testid="booking-modal">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden
      />

      <div className="relative bg-white w-full max-w-2xl rounded-3xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-[var(--ob-border)] flex items-center justify-between px-6 py-4 rounded-t-3xl">
          <div>
            <div className="text-[13px] tracking-[0.14em] uppercase" style={{ color: "var(--ob-muted)" }}>
              Strategy call
            </div>
            <div className="text-[20px] font-light" style={{ color: "var(--ob-ink)" }}>
              Book a time with Aimee
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="booking-close"
            className="w-10 h-10 rounded-full border border-[var(--ob-border)] inline-flex items-center justify-center hover:bg-[var(--ob-bg-alt)]"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 lg:p-8">
          {done ? (
            <div className="py-12 text-center" data-testid="booking-success">
              <div className="ob-h3 mb-4">Booking request sent.</div>
              <p className="ob-body max-w-md mx-auto">
                Aimee will confirm the time by email at {form.email || "your inbox"}. If anything urgent, feel free to reply directly to aimee@onboard.nz.
              </p>
              <button onClick={onClose} className="ob-btn ob-btn-primary mt-8">Close</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="booking-form">
              <div>
                <div className="ob-eyebrow mb-3 inline-flex items-center gap-2"><CalendarDays size={14} strokeWidth={1.5} /> Choose a day</div>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {days.map((d) => {
                    const selected = date && d.toDateString() === new Date(date).toDateString();
                    return (
                      <button
                        type="button"
                        key={d.toISOString()}
                        onClick={() => setDate(d.toISOString())}
                        data-testid={`booking-date-${d.toISOString().slice(0,10)}`}
                        className={`shrink-0 min-w-[92px] rounded-2xl border px-4 py-3 text-left transition-colors ${
                          selected
                            ? "border-[var(--ob-ink)] bg-[var(--ob-ink)] text-white"
                            : "border-[var(--ob-border)] hover:border-[var(--ob-ink)]"
                        }`}
                      >
                        <div className="text-[11px] tracking-[0.14em] uppercase opacity-70">
                          {d.toLocaleDateString("en-NZ", { weekday: "short" })}
                        </div>
                        <div className="text-[18px] font-light mt-1">
                          {d.toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="ob-eyebrow mb-3 inline-flex items-center gap-2"><Clock size={14} strokeWidth={1.5} /> Pick a time (NZT)</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setTime(t)}
                      data-testid={`booking-time-${t}`}
                      className={`h-11 rounded-full border text-[14px] transition-colors ${
                        time === t
                          ? "border-[var(--ob-ink)] bg-[var(--ob-ink)] text-white"
                          : "border-[var(--ob-border)] hover:border-[var(--ob-ink)]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <input required data-testid="booking-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full h-12 px-5 rounded-full border border-[var(--ob-border)] bg-white text-[15px]" />
                <input required type="email" data-testid="booking-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className="w-full h-12 px-5 rounded-full border border-[var(--ob-border)] bg-white text-[15px]" />
                <input data-testid="booking-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="w-full h-12 px-5 rounded-full border border-[var(--ob-border)] bg-white text-[15px]" />
                <input data-testid="booking-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company (optional)" className="w-full h-12 px-5 rounded-full border border-[var(--ob-border)] bg-white text-[15px]" />
              </div>

              <textarea data-testid="booking-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything you'd like Aimee to know before the call?" rows={3} className="w-full p-5 rounded-3xl border border-[var(--ob-border)] bg-white text-[15px] resize-none" />

              <div className="flex items-center justify-between gap-3 pt-2">
                <div className="text-[13px]" style={{ color: "var(--ob-muted)" }}>
                  Confirmations go to aimee@onboard.nz
                </div>
                <button disabled={submitting} type="submit" className="ob-btn ob-btn-primary h-12 px-8" data-testid="booking-submit">
                  {submitting ? "Sending..." : "Request booking"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
